// @flow

import React from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import type { State } from "@store/reducers";
import actionCreators from "@modules/reporting/actionCreator";
import {
  getAppealsFiltersSchema,
  getAppealReasons,
} from "@modules/reporting/selectors/reports/appeals";
import type { AddEditModalConfig } from "@modules/reporting/reducer/modals/types";
import { ReportModalStepTypes } from "@modules/reporting/constants/reports";
import type {
  DefaultReport,
  MySavedReport,
  AddEditReportModalType,
  SaveReportModalType,
} from "@modules/reporting/reducer/modals/types";
import { getAddEditReportModal, getSaveReportModal } from "@modules/reporting/selectors/modals";
import { cloneObject } from "@utils/object";
import AddEditReportModal from "../add-edit-report-modal";
import SaveReportModal from "../save-report-modal";
import StepFiltersView from "./step-filters-view";
import config from "@config";

type OwnProps = $Exact<{
  clientId: number,
  returnReportToSave?: (data: MySavedReport) => void,
  getReport?: (data: MySavedReport) => () => void,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  appealReasons: Array<string>,
  addEditModal: AddEditReportModalType,
  saveReportModal: SaveReportModalType,
}>;

type PropsFromDispatch = $Exact<{
  getAppealsFiltersSchema: typeof actionCreators.GetAppealsFiltersSchema,
  getAppealsReasons: typeof actionCreators.GetAppealsReasons,
  createAppealsReport: typeof actionCreators.CreateAppealsReport,
  updateAppealsReport: typeof actionCreators.UpdateAppealsReport,
  setReportToSave: typeof actionCreators.SetAppealsReportToSave,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

type ComponentState = $Exact<{
  data: Object,
}>;

export class AppealsAddEditReportModal extends React.Component<Props, ComponentState> {
  state = {
    data: {},
  };

  componentDidMount() {
    this.props.getAppealsFiltersSchema({ clientId: this.props.clientId });
    this.props.getAppealsReasons({ clientId: this.props.clientId });
  }

  onUpdateAppealsReport = (data: MySavedReport) => {
    if (this.props.returnReportToSave) {
      this.props.returnReportToSave(data);
      this.props.toggleAddEditReportModal({ visible: false });
      return;
    }

    this.props.updateAppealsReport({
      clientId: this.props.clientId,
      reportId: data.id,
      data,
    });
    this.props.toggleAddEditReportModal({ visible: false });
  };

  onCreateAppealsReport = (data: DefaultReport) => {
    this.props.createAppealsReport({
      clientId: this.props.clientId,
      ...data,
      columns: this.props?.columns || data.columns,
    });
  };

  onGetReport = (data: MySavedReport) => {
    if (!this.props.getReport) {
      return;
    }

    this.props.getReport(data)();
    this.props.setReportToSave({
      ...data,
      isNew: true,
    });
    this.props.toggleAddEditReportModal({ visible: false });
  };

  getFiltersView = (data: Object) => {
    return <StepFiltersView data={data} />;
  };

  get filtersSchema() {
    if (!this.props.schema) {
      return { type: "object", properties: {} };
    }

    const _schema = cloneObject(this.props.schema);

    if (_schema.properties?.appealReasons?.items && this.props.appealReasons.length > 0) {
      _schema.properties.appealReasons.items = {
        ..._schema.properties.appealReasons.items,
        oneOf: this.props.appealReasons.map(reason => ({
          const: reason,
          title: reason,
        })),
      };
    }

    return _schema;
  }

  get modalConfig(): AddEditModalConfig {
    return [
      {
        type: ReportModalStepTypes.FILTERS,
        title: {
          new: this.props.t("SET_FILTERS"),
          edit: this.props.t("FILTERS"),
        },
        schema: this.filtersSchema,
        uiSchema: this.getStepFiltersUiSchema(this.props.t),
        getView: this.getFiltersView,
        hideNextButton: true,
      },
    ];
  }

  get isEditModal(): boolean {
    return !!this.props.addEditModal.data;
  }

  getStepFiltersUiSchema = (t: Function) => {
    return {
      // Period
      dateRange: {
        "ui:header": this.isEditModal && t("PERIOD"),
        "ui:options": {
          tooltip: {
            title: t("REPORT_FILTER_DATE_RANGE"),
            description: t("DATE_RANGE_TOOLTIP"),
            id: "appeals-report-date-range",
            size: "medium",
          },
        },
      },
      fromDate: {
        "ui:title": t("FROM_DATE"),
        "ui:widget": "date-picker",
        "ui:options": { dateFormat: "YYYY-MM-DD" },
      },
      toDate: {
        "ui:title": t("TO_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
        },
      },
      // Filters
      outcome: {
        "ui:header": this.isEditModal && t("STATUS"),
        items: {
          "ui:options": {
            hidelabel: true,
          },
        },
        "ui:field": "multi-select",
      },
      kpiStatus: {
        "ui:widget": "select",
      },
      appealReasons: {
        items: {
          "ui:options": {
            hidelabel: true,
          },
        },
        "ui:field": "multi-select",
      },
      staffGroups: {
        "ui:header": this.isEditModal && t("EMPLOYEES"),
        items: {
          id: {
            "ui:options": {
              hidelabel: true,
            },
          },
        },
        "ui:field": "multi-select",
      },
      "ui:order": this.isEditModal
        ? [
            // Period
            "dateRange",
            "fromDate",
            "toDate",
            // Filters
            "outcome",
            "kpiStatus",
            "appealReasons",
            "staffGroups",
            "*",
          ]
        : [
            "dateRange",
            "outcome",
            "fromDate",
            "kpiStatus",
            "toDate",
            "appealReasons",
            "staffGroups",
            "*",
          ],
    };
  };

  render() {
    return (
      <React.Fragment>
        {this.props.addEditModal.visible && (
          <AddEditReportModal
            getReport={this.onGetReport}
            initialStep={this.props.addEditModal.initialStep}
            updateReport={this.onUpdateAppealsReport}
            clientId={this.props.clientId}
            modalConfig={this.modalConfig}
            onChange={(data: Object) => {
              this.setState({ data });
            }}
          />
        )}
        {this.props.saveReportModal.visible && (
          <SaveReportModal
            createReport={this.onCreateAppealsReport}
            updateReport={this.onUpdateAppealsReport}
            columns={this.props.columns}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  schema: getAppealsFiltersSchema(state),
  appealReasons: getAppealReasons(state),
  addEditModal: getAddEditReportModal(state),
  saveReportModal: getSaveReportModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAppealsFiltersSchema: actionCreators.GetAppealsFiltersSchema,
  getAppealsReasons: actionCreators.GetAppealsReasons,
  createAppealsReport: actionCreators.CreateAppealsReport,
  updateAppealsReport: actionCreators.UpdateAppealsReport,
  setReportToSave: actionCreators.SetAppealsReportToSave,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AppealsAddEditReportModal);
